import { useEffect } from 'react'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

import { digObject, getCookies } from 'functions/utilities'

import useAgency from 'agency/hooks/useAgency'
import useUser from 'agency/hooks/useUser'

export const mixpanelDefaultConfig = {
  api_transport: 'sendBeacon',
  batch_requests: true,
  debug: process.env.NODE_ENV !== 'production',
  persistence: 'localStorage',
}

export const mixpanelDefaultSuperProperties = {
  Product: 'Realhub',
}

const dispatchMixpanelEvent = (params) => {
  const {
    customOptions = {}, defaultProperties, eventType,
  } = params

  if (!eventType) return null

  return mixpanel.track(eventType, {
    ...defaultProperties,
    ...customOptions,
  })
}

const initMixpanel = (params) => {
  const {
    defaultProperties, mixpanelToken, shouldAutoCapture, shouldTrackPageView,
  } = params

  if (!mixpanelToken) return

  const { isAdmin, userId } = defaultProperties

  const allowAdvancedTracking = shouldAutoCapture && !isAdmin

  const mergedProperties = {
    ...mixpanelDefaultSuperProperties,
    ...defaultProperties,
  }

  mixpanel.init(
    mixpanelToken,
    {
      ...mixpanelDefaultConfig,
      autocapture: allowAdvancedTracking ? {
        capture_text_content: true,
        click: true,
        input: true,
        pageview: 'full-url',
        scroll: true,
        submit: true,
      } : false,
      // eslint-disable-next-line max-len
      record_sessions_percent: allowAdvancedTracking ? 1 : 0, // As per Mixpanel's recommendation we should start with 1% and move up
      track_pageview: shouldTrackPageView,
    },
  )

  // identify need to be defined before building people's properties
  mixpanel.identify(`realhub_${userId}`)
  mixpanel.people.set(mergedProperties)

  // Automatically start Session Recording
  if (allowAdvancedTracking){
    mixpanel.start_session_recording()
  }
}

function useMixpanel(options){
  const { shouldAutoCapture = true, shouldInitialise = true, shouldTrackPageView = true } = options || {}

  const currentUser = useSelector(reduxState => reduxState.currentUser.result)

  const { association, association_type } = currentUser || {}

  const currentUserPayload = useUser(currentUser)
  const {
    user: { id },
    isAdmin,
    isAgencyAdmin,
    isAgencyUser,
    isAgent,
    isBrandUser,
    isProviderDirector,
    isProviderUser,
    provider,
    roleTitle,
  } = currentUserPayload

  const initAgency = association_type === 'Agency' ? association : {}
  const { agency, brand } = useAgency(initAgency)

  const { admin_auth_token } = getCookies()

  const defaultProperties = {
    agency: agency?.title || 'N/A',
    agencyState: agency?.address_state || 'N/A',
    brand: brand?.title || 'N/A',
    brandId: brand?.id || agency?.brand_id || {},
    isAdmin,
    isAgencyAdmin,
    isAgencyUser,
    isAgent,
    isBrandUser,
    // isImpersonatingUser: !!admin_auth_token,
    isProviderDirector,
    isProviderUser,
    legacy: true,
    provider: provider?.title || 'N/A',
    role: roleTitle,
    userId: id,
  }

  const mixpanelToken = digObject(window, 'publicApiKeys.mixpanelToken')
  || digObject(window, 'dashboardSettings.publicApiKeys.mixpanelToken')
  || digObject(window, 'dashboardOptions.publicApiKeys.mixpanelToken')
  || digObject(window, 'builderOptions.publicApiKeys.mixpanelToken')

  useEffect(() => {
    if (currentUser?.id && shouldInitialise && mixpanelToken){
      // Initializing Mixpanel
      initMixpanel({
        defaultProperties, mixpanelToken, shouldAutoCapture, shouldTrackPageView,
      })
    }
  }, [currentUser?.id, shouldInitialise, JSON.stringify(defaultProperties)])

  return {
    callbacks: {
      dispatchMixpanelEvent: (eventType, customOptions) => dispatchMixpanelEvent({
        customOptions,
        defaultProperties,
        eventType,
      }),
      initMixpanel: () => initMixpanel({
        defaultProperties, mixpanelToken, shouldAutoCapture, shouldTrackPageView,
      }),
    },
  }
}

export default useMixpanel
